import React from "react";
import { Image, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import type { MiContenidoMetricsExportData } from "../miContenidoExportData";
import { getPdfWordmarkSrc } from "./pdfAssets";
import {
  METRICS_PDF_DECK_CONTENT_PADDING_BOTTOM_WITH_FOOTER_NOTE,
  METRICS_PDF_DECK_CONTENT_PADDING_Y,
  METRICS_PDF_DECK_FOOTER_NOTE_BLOCK_PT,
  METRICS_PDF_DECK_HEADER_RESERVED_PT,
  METRICS_PDF_DECK_PADDING_HORIZONTAL,
  METRICS_PDF_DECK_PROGRAM_CONTENT_PADDING_TOP,
  METRICS_PDF_FOOTER_RESERVED_PT,
  METRICS_PDF_PAGE_SIZE,
} from "./constants";
import { MetricsPdfDeckPageHeader } from "./MetricsPdfDeckPageHeader";
import { usePdfTheme } from "./PdfThemeContext";
import { PDF_FONT, PDF_FONT_FAMILY } from "./pdfTheme";

const PODSCOPE_WORDMARK_HEIGHT_PT = 12;
const PODSCOPE_WORDMARK_WIDTH_PT = (PODSCOPE_WORDMARK_HEIGHT_PT * 793) / 186;

type Props = {
  branding: MiContenidoMetricsExportData["branding"];
  generated: string;
  headerTitle: string;
  headerPeriodLabel?: string;
  headerSubtitle?: string;
  headerImageUrl?: string;
  /** Default: {@link METRICS_PDF_DECK_CONTENT_PADDING_Y}. */
  contentPaddingTop?: number;
  contentPaddingBottom?: number;
  /** Shown above the footer divider (e.g. Desglose por plataforma explainer). */
  footerNote?: string;
  children: React.ReactNode;
};

function MetricsPdfDeckFooter({
  branding,
  generated,
}: {
  branding: MiContenidoMetricsExportData["branding"];
  generated: string;
}) {
  const { t } = useTranslation("metricsPdf");
  const theme = usePdfTheme();
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        footerWrap: {
          flexShrink: 0,
        },
        divider: {
          height: 1,
          width: "100%",
          backgroundColor: theme.borderDivider,
        },
        footer: {
          height: METRICS_PDF_FOOTER_RESERVED_PT,
          paddingHorizontal: METRICS_PDF_DECK_PADDING_HORIZONTAL,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          fontFamily: PDF_FONT_FAMILY,
        },
        footerLogo: {
          width: 36,
          height: 36,
          borderRadius: 8,
          objectFit: "contain",
        },
        footerTextWrap: {
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          marginLeft: 10,
        },
        footerText: {
          fontSize: 12,
          color: theme.textMutedAlt,
        },
        footerBrandMark: {
          height: PODSCOPE_WORDMARK_HEIGHT_PT,
          width: PODSCOPE_WORDMARK_WIDTH_PT,
          objectFit: "contain",
          marginHorizontal: 2,
          // Optical align with caption baseline (wordmark sits slightly high).
          marginTop: 2,
        },
        footerLine: {
          flexDirection: "row",
          alignItems: "center",
        },
      }),
    [theme],
  );

  const custom = branding.footerText?.trim();
  const wordmarkSrc = getPdfWordmarkSrc(theme.mode);

  const footerText = custom ? (
    <Text
      style={styles.footerText}
      render={({ pageNumber, totalPages }) => {
        const pageSuffix =
          totalPages > 1
            ? t("pageSuffix", { page: pageNumber, total: totalPages })
            : "";
        return `${custom}${pageSuffix}`;
      }}
    />
  ) : (
    <View style={styles.footerLine}>
      <Text style={styles.footerText}>{t("reportGeneratedBy")} </Text>
      <Image src={wordmarkSrc} style={styles.footerBrandMark} />
      <Text
        style={styles.footerText}
        render={({ pageNumber, totalPages }) => {
          const pageSuffix =
            totalPages > 1
              ? t("pageSuffix", { page: pageNumber, total: totalPages })
              : "";
          return ` ${t("generatedEl", { date: generated })}${pageSuffix}`;
        }}
      />
    </View>
  );

  return (
    <View style={styles.footerWrap}>
      <View style={styles.divider} />
      <View style={styles.footer}>
        <View>
          {branding.companyLogoUrl ? (
            <Image src={branding.companyLogoUrl} style={styles.footerLogo} />
          ) : null}
        </View>
        <View style={styles.footerTextWrap}>{footerText}</View>
      </View>
    </View>
  );
}

export function metricsPdfDeckContentHeight(
  paddingTop: number,
  paddingBottom: number,
): number {
  return (
    METRICS_PDF_PAGE_SIZE[1] -
    METRICS_PDF_DECK_HEADER_RESERVED_PT -
    METRICS_PDF_FOOTER_RESERVED_PT -
    paddingTop -
    paddingBottom
  );
}

/** Usable inner content height inside a deck page shell. */
export const METRICS_PDF_DECK_CONTENT_HEIGHT = metricsPdfDeckContentHeight(
  METRICS_PDF_DECK_CONTENT_PADDING_Y,
  METRICS_PDF_DECK_CONTENT_PADDING_Y,
);

export const METRICS_PDF_DECK_PROGRAM_CONTENT_HEIGHT = metricsPdfDeckContentHeight(
  METRICS_PDF_DECK_PROGRAM_CONTENT_PADDING_TOP,
  METRICS_PDF_DECK_CONTENT_PADDING_Y,
);

/** Program page content height when a footer note sits above the footer divider. */
export const METRICS_PDF_DECK_PROGRAM_CONTENT_HEIGHT_WITH_FOOTER_NOTE =
  metricsPdfDeckContentHeight(
    METRICS_PDF_DECK_PROGRAM_CONTENT_PADDING_TOP,
    METRICS_PDF_DECK_CONTENT_PADDING_BOTTOM_WITH_FOOTER_NOTE,
  ) - METRICS_PDF_DECK_FOOTER_NOTE_BLOCK_PT;

export function MetricsPdfDeckPage({
  branding,
  generated,
  headerTitle,
  headerPeriodLabel,
  headerSubtitle,
  headerImageUrl,
  contentPaddingTop,
  contentPaddingBottom,
  footerNote,
  children,
}: Props): React.ReactElement {
  const theme = usePdfTheme();
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        page: {
          padding: 0,
          fontFamily: PDF_FONT_FAMILY,
          color: theme.textBody,
        },
        shell: {
          width: METRICS_PDF_PAGE_SIZE[0],
          height: METRICS_PDF_PAGE_SIZE[1],
          flexDirection: "column",
          backgroundColor: theme.pageBg,
        },
        content: {
          flex: 1,
          flexDirection: "column",
          paddingHorizontal: METRICS_PDF_DECK_PADDING_HORIZONTAL,
          minHeight: 0,
        },
        footerNote: {
          flexShrink: 0,
          paddingHorizontal: METRICS_PDF_DECK_PADDING_HORIZONTAL,
          paddingTop: 4,
          paddingBottom: 10,
          height: METRICS_PDF_DECK_FOOTER_NOTE_BLOCK_PT,
          justifyContent: "flex-start",
        },
        footerNoteText: {
          fontSize: 11,
          lineHeight: 1.35,
          color: theme.textMutedAlt,
          fontFamily: PDF_FONT_FAMILY,
        },
        footerNoteMark: {
          fontWeight: PDF_FONT.bold,
          color: theme.textMutedAlt,
        },
      }),
    [theme],
  );

  const padTop = contentPaddingTop ?? METRICS_PDF_DECK_CONTENT_PADDING_Y;
  const padBottom = footerNote
    ? (contentPaddingBottom ??
      METRICS_PDF_DECK_CONTENT_PADDING_BOTTOM_WITH_FOOTER_NOTE)
    : (contentPaddingBottom ?? METRICS_PDF_DECK_CONTENT_PADDING_Y);

  return (
    <Page size={METRICS_PDF_PAGE_SIZE} style={styles.page} wrap={false}>
      <View style={styles.shell}>
        <MetricsPdfDeckPageHeader
          title={headerTitle}
          {...(headerPeriodLabel ? { periodLabel: headerPeriodLabel } : {})}
          {...(headerSubtitle ? { subtitle: headerSubtitle } : {})}
          {...(headerImageUrl ? { imageUrl: headerImageUrl } : {})}
        />
        <View
          style={[
            styles.content,
            { paddingTop: padTop, paddingBottom: padBottom },
          ]}
        >
          {children}
        </View>
        {footerNote ? (
          <View style={styles.footerNote}>
            <Text style={styles.footerNoteText}>
              <Text style={styles.footerNoteMark}>* </Text>
              {footerNote}
            </Text>
          </View>
        ) : null}
        <MetricsPdfDeckFooter branding={branding} generated={generated} />
      </View>
    </Page>
  );
}
